import { useState, FormEvent } from 'react'
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js'
import type { SetupIntent, StripeError } from '@stripe/stripe-js'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle } from 'lucide-react'

interface PaymentFormProps {
  onSuccess: (setupIntent: SetupIntent) => void
  onError?: (error: StripeError) => void
}

export function PaymentForm({ onSuccess, onError }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          // Return URL is required but we use redirect: 'if_required'
          // so it will only redirect if 3D Secure requires it
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })

      if (error) {
        // Show error to customer
        setErrorMessage(getErrorMessage(error))
        onError?.(error)
      } else if (setupIntent) {
        // Success - payment method is now attached
        onSuccess(setupIntent)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      setErrorMessage(message)
    } finally {
      setIsLoading(false)
    }
  }

  // Stripe or Elements not yet loaded
  if (!stripe || !elements) {
    return null
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-lg border p-4">
        <PaymentElement
          options={{
            layout: 'tabs',
            wallets: {
              link: 'never',
            },
          }}
        />
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        disabled={!stripe || isLoading}
        className="w-full"
        size="lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          'Subscribe Now'
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Your payment is securely processed by Stripe. We never store your card details.
      </p>
    </form>
  )
}

// Map Stripe error codes to user-friendly messages
function getErrorMessage(error: StripeError): string {
  switch (error.code) {
    case 'card_declined':
      return 'Your card was declined. Please try a different card.'
    case 'expired_card':
      return 'Your card has expired. Please use a different card.'
    case 'incorrect_cvc':
      return 'The security code (CVC) is incorrect. Please check and try again.'
    case 'insufficient_funds':
      return 'Your card has insufficient funds. Please use a different card.'
    case 'processing_error':
      return 'An error occurred while processing your card. Please try again.'
    case 'incorrect_number':
      return 'The card number is incorrect. Please check and try again.'
    default:
      return error.message || 'An error occurred. Please try again.'
  }
}
